import { defineNuxtPlugin, useRuntimeConfig } from '#imports'

/**
 * Origem Flow: notificações de sistema no desktop (Web Push).
 *
 * Registra o service worker (/sw.js), inscreve o navegador no push com a
 * chave VAPID pública e registra a inscrição no backend, que envia um push
 * para cada notificação do sininho (menções, comentários, atribuições).
 *
 * Comportamento:
 * - permissão já concedida + usuário logado → (re)inscreve em silêncio a
 *   cada carga do app, mantendo o backend atualizado;
 * - permissão ainda não pedida → o painel de notificações mostra o botão
 *   "Ativar notificações neste dispositivo", que chama $webPush.enable();
 * - logout → desfaz a inscrição do dispositivo (parar de receber push de
 *   uma conta que saiu).
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

class WebPush {
  constructor(nuxtApp, vapidPublicKey) {
    this.nuxtApp = nuxtApp
    this.vapidPublicKey = vapidPublicKey
  }

  get isSupported() {
    return (
      !!this.vapidPublicKey &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  get permission() {
    return 'Notification' in window ? Notification.permission : 'denied'
  }

  async getRegistration() {
    if (!this._registrationPromise) {
      this._registrationPromise = navigator.serviceWorker.register('/sw.js')
    }
    return await this._registrationPromise
  }

  async getSubscription() {
    const registration = await this.getRegistration()
    return await registration.pushManager.getSubscription()
  }

  async isEnabled() {
    if (!this.isSupported || this.permission !== 'granted') {
      return false
    }
    return (await this.getSubscription()) !== null
  }

  /**
   * Pede a permissão (se necessário), inscreve o navegador e registra no
   * backend. Retorna true se ficou ativo.
   */
  async enable() {
    if (!this.isSupported) {
      return false
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return false
    }

    const registration = await this.getRegistration()
    let subscription = await registration.pushManager.getSubscription()
    if (subscription === null) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(this.vapidPublicKey),
      })
    }

    await this.nuxtApp.$client.post('/push/subscriptions/', {
      kind: 'webpush',
      endpoint: subscription.endpoint,
      p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
      auth: arrayBufferToBase64Url(subscription.getKey('auth')),
    })
    return true
  }

  /**
   * Desfaz a inscrição do dispositivo (local e no backend).
   */
  async disable() {
    if (!this.isSupported) {
      return
    }
    try {
      const subscription = await this.getSubscription()
      if (subscription !== null) {
        await this.nuxtApp.$client.delete('/push/subscriptions/', {
          data: { endpoint: subscription.endpoint },
        })
        await subscription.unsubscribe()
      }
    } catch (e) {
      // Melhor esforço: sem rede, a inscrição morta é limpa pelo backend
      // no próximo envio que falhar.
    }
  }

  /**
   * Reinscreve em silêncio quando a permissão já foi concedida antes.
   */
  async resubscribeIfGranted() {
    if (!this.isSupported || this.permission !== 'granted') {
      return
    }
    try {
      await this.enable()
    } catch (e) {
      // Silencioso: push é acessório, nunca pode quebrar a carga do app.
    }
  }
}

export default defineNuxtPlugin({
  name: 'webPush',
  dependsOn: ['store', 'client-handler'],
  setup(nuxtApp) {
    if (!import.meta.client) {
      return
    }

    const config = useRuntimeConfig().public
    const webPush = new WebPush(nuxtApp, config.vapidPublicKey || '')
    nuxtApp.provide('webPush', webPush)

    const store = nuxtApp.$store

    // (Re)inscreve em silêncio quando o app carrega já logado.
    nuxtApp.hook('app:mounted', () => {
      if (store.getters['auth/isAuthenticated']) {
        webPush.resubscribeIfGranted()
      }
    })

    // Logout → o dispositivo deixa de receber push da conta que saiu.
    store.subscribe((mutation) => {
      if (mutation.type === 'auth/LOGOFF') {
        webPush.disable()
      }
    })
  },
})
